import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import { useLocation, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../hooks';
import { TPTextField } from '../../components/Inputs';
import { getAuthState } from '../../store/AuthSlice';
import { userSignIn } from '../../store/AuthSlice/V2/AuthApi';
import { TP_COLORS } from '../../constants';

type TState = {
  email: string;
  password: string;
  isPasswordVisible: boolean;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  email: '',
  password: '',
  isPasswordVisible: false,
};

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onSignUpClick: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  open,
  onClose,
  onSignUpClick,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();

  // Store
  const { status } = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [searchParams, setSearchParams] = useSearchParams();

  const isMdOrMore = useMediaQuery(theme.breakpoints.up('md'));
  const isSubmitDisabled =
    !state.email.trim().length ||
    !state.password.trim().length ||
    status === 'loading';

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setState((prevState) => ({
      ...prevState,
      isPasswordVisible: !prevState.isPasswordVisible,
    }));
  };

  const addRedirectParam = () => {
    const newParams = new URLSearchParams(searchParams);
    const redirectURL = location.pathname
      .replace('/learning-portal', '')
      .trim();
    newParams.set('redirect', redirectURL);
    setSearchParams(newParams);
  };

  const onSubmit = () => {
    addRedirectParam();

    dispatch(
      userSignIn({
        username: state.email.toLowerCase(),
        password: state.password,
      })
    );
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '30px',
          borderRadius: '20px',
        },
      }}
    >
      <Grid container spacing={2} alignItems="center">
        {isMdOrMore ? (
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <div style={{ marginBottom: '15px', marginTop: '75px' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                color="#006C9C"
                lineHeight="48.41px"
                fontSize="40px"
              >
                Unlock <span style={{ color: '#01A79F' }}>Knowledge</span>
                <br />
                Sign In Now
              </Typography>
            </div>
            <div>
              <Typography
                fontWeight={500}
                fontSize="16px"
                lineHeight="19.36px"
                color="#334155"
                maxWidth="464px"
              >
                Your journey with us continues. Sign in and explore a world of
                possibilities. We&apos;re delighted to have you here!
              </Typography>
            </div>
            <Stack direction="row" justifyContent="flex-end" marginTop="-140px">
              <img
                src={`${process.env.PUBLIC_URL}/images/login.png`}
                alt="tp-logo"
                style={{ width: '100%', maxWidth: '595px' }}
              />
            </Stack>
          </Grid>
        ) : null}
        <Grid item xs={12} sm={12} md={12} lg={6}>
          <div
            style={{
              maxWidth: '400px',
              margin: '0 auto',
            }}
          >
            <div style={{ marginBottom: '10px' }}>
              <Typography
                variant="h6"
                fontWeight={700}
                fontSize="30px"
                lineHeight="36.3px"
                textAlign="center"
                color={TP_COLORS.black}
              >
                Welcome back
              </Typography>
            </div>
            <div style={{ marginBottom: '30px' }}>
              <Typography
                variant="h6"
                fontWeight={400}
                fontSize="14px"
                lineHeight="17px"
                textAlign="center"
                color={TP_COLORS.black}
              >
                Please enter your email address and password to access your
                account.
              </Typography>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      name="email"
                      label="Email Address"
                      value={state.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...textFieldProps}
                      type={state.isPasswordVisible ? 'text' : 'password'}
                      name="password"
                      label="Password"
                      value={state.password}
                      onChange={handleInputChange}
                      placeholder="Password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePasswordVisibility}>
                              {state.isPasswordVisible ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitDisabled}
                    loading={status === 'loading'}
                  >
                    Sign In
                  </LoadingButton>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    fontWeight={500}
                    color="#647488"
                    fontSize="14px"
                    lineHeight="16.94px"
                    textAlign="center"
                  >
                    Don&apos;t have an account ?{' '}
                    <Button
                      variant="text"
                      onClick={() => {
                        onSignUpClick();
                        onClose();
                      }}
                      style={{
                        textDecoration: 'underline',
                      }}
                    >
                      Register with us
                    </Button>
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </div>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default LoginModal;
